import { useCallback, useRef } from "react";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

/**
 * Returns a stable fetch wrapper for the same-origin Worker API.
 * The Kinde access token is attached when signed in; the Worker decides what the viewer may see.
 */
export function useApi() {
  const kinde = useKindeAuth(),
    current = useRef(kinde);
  current.current = kinde;
  return useCallback(async (path, { json, headers, ...init } = {}) => {
    const finalHeaders = new Headers(headers);
    const auth = current.current;
    if (auth.isAuthenticated) {
      let token;
      try {
        token = await auth.getAccessToken();
      } catch {
        throw new ApiError(401, "Your session expired. Please sign in again.");
      }
      if (!token)
        throw new ApiError(401, "Your session expired. Please sign in again.");
      if (
        current.current.user?.id !== auth.user?.id ||
        !current.current.isAuthenticated
      )
        throw new DOMException("Account changed", "AbortError");
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
    if (json !== undefined)
      finalHeaders.set("Content-Type", "application/json");

    const response = await fetch(path, {
      ...init,
      headers: finalHeaders,
      body: json !== undefined ? JSON.stringify(json) : init.body,
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        message = (await response.json()).error || message;
      } catch {
        // non-JSON error body
      }
      throw new ApiError(response.status, message);
    }
    return response;
  }, []);
}

export function formatCount(n) {
  if (!n) return "0";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}
